import { useEffect, useRef } from 'react'

import type { FilterStep, PhotoAdjust } from '../lib/photoEdit'

/**
 * A photo with its edit, drawn into a canvas (1.178.0): the collage's
 * graded tile and a preset's thumbnail. The canvas keeps the photo's size;
 * what fits it into its place is the stylesheet, as with an `<img>`.
 *
 * `data-drawn` counts the drawings, and `data-draw-way` names the way
 * (`gpu`, `cpu`) -- what a run waits for and reads. Until the first drawing
 * the canvas is empty and sees through to what lies below it.
 */
export function EditedCanvas({
  src,
  adjust,
  filters,
  className,
  marks,
}: {
  src: string
  adjust: PhotoAdjust
  filters: Record<string, FilterStep[]> | undefined
  className?: string
  /** `data-…` marks for the runs. */
  marks?: Record<string, string>
}) {
  const ref = useRef<HTMLCanvasElement>(null)
  const key = JSON.stringify([src, adjust, filters === undefined])
  useEffect(() => {
    const canvas = ref.current
    if (canvas === null) return
    let current = true
    // The drawing (WebGL, the colour table) is loaded with the first
    // graded picture (1.187.0, review 2026-10b D1); the chat starts
    // without it. A drawing asked for and gone before it loaded is none.
    import('../lib/editedPicture').then((m) => {
      if (!current) throw new Error('vorbei')
      return m.drawEdited(canvas, src, adjust, filters)
    }).then(
      (way) => {
        if (!current) return
        canvas.dataset['drawn'] = String(Number(canvas.dataset['drawn'] ?? '0') + 1)
        canvas.dataset['drawWay'] = way
      },
      () => undefined,
    )
    return () => {
      current = false
    }
    // `key` stands for src, adjust and whether the filters are there.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  return <canvas ref={ref} className={className} aria-hidden="true" {...marks} />
}
